import React from 'react';
import {
  createContainer,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
} from 'victory';
import { v4 as uuidv4 } from 'uuid';

interface ChartLineComponentProps {
  idChart: number;
  data: any;
  chartStyles: object;
  chartTheme?: any;
  verticalLine?: any[];
  stresHold?: object[];
  chartResolution?: any;
  mainTitleParams: any;
  secondTitleParams?: any;
  yLabel?: string;
  xLabel?: string;
  dataDates?: object[];
}

const axisTitleStyles = {
  axisLabel: { padding: 35, fontSize: "10px" },
  tickLabels: { fontWeight: 400, fontSize: 10 },
  ticks: { stroke: "grey", size: 5 },
};

const lineStyles = {
  data: { fill: '#008BD2' },
  labels: { fill: '#008BD2', fontSize: 12 },
};

const eventsCss: any = [
  {
    target: 'data',
    eventHandlers: {
      onMouseOver: () => {
        return [
          {
            target: 'data',
            mutation: () => ({
              style: { fill: 'gold', width: 30 },
            }),
          },
          {
            target: 'labels',
            mutation: () => ({ active: true }),
          },
        ];
      },
      onMouseOut: () => {
        return [
          {
            target: 'data',
            mutation: () => {},
          },
          {
            target: 'labels',
            mutation: () => ({ active: false }),
          },
        ];
      },
    },
  },
];

const verticalLinesRenders = (verticalLine: any[]) => {
  return verticalLine.map((vLine) => {
    return (
      <VictoryLine
        data={[
          { x: vLine[0].x, y: vLine[0].y },
          { x: vLine[1].x, y: vLine[1].y },
        ]}
        style={{
          data: {
            stroke: '#008BD2',
            strokeWidth: 2,
            strokeDasharray: '5 10'
          },
        }}
        key={uuidv4()}
      />
    );
  });
};

const ChartLineComponent = (props: ChartLineComponentProps) => {
   const VictoryZoomVoronoiContainer = createContainer('zoom', 'voronoi') as React.ComponentType<any>;
   const getMaxValue = props.data.line.length !== 0 ? Math.max(...props.data.line.map((d: any) => d.y)) : 0;   
  return (
    <>
      {props.data.line.length !== 0 ? (
        <VictoryChart
          containerComponent={<VictoryZoomVoronoiContainer />}
          theme={props.chartTheme !== undefined ? props.chartTheme : undefined}
          style={props.chartStyles}
          height={
            props.chartResolution !== undefined
              ? props.chartResolution.height
              : undefined
          }
          width={
            props.chartResolution !== undefined
              ? props.chartResolution.width
              : undefined
          }
          scale={{ x: "time", y: "linear" }}
        >
          {/* Axis X & Y */}
          {/* Y */}
          <VictoryAxis
            dependentAxis
            tickFormat={(t) => `${t.toFixed(1)}`}
            tickValues={[
              0,
              getMaxValue / 4,
              getMaxValue / 2,
              (getMaxValue / 4) * 3,
              getMaxValue,
            ]}
            style={{
              ticks: { stroke: "grey", size: 5 },
              tickLabels: {
                fontWeight: 400,
                fontSize: 10,
              },
            }}
          />
          <VictoryLabel
            y={30}
            text={props.yLabel !== undefined ? props.yLabel : ""}
            style={{ fontSize: "10px" }}
          />
          {/* X */}
          <VictoryAxis
            label={props.xLabel !== undefined ? props.xLabel : ""}
            style={axisTitleStyles}
            tickFormat={(date) => {
              return date
                .toLocaleString("default", {
                  day: "numeric",
                  month: "short",
                })
                .concat(
                  "\n" +
                    date.toLocaleString("default", {
                      hour: "numeric",
                    })
                );
            }}
          />
          {/* Labels */}
          <VictoryLabel
            x={props.mainTitleParams.x}
            y={props.mainTitleParams.y}
            text={props.mainTitleParams.title}
          />
          <VictoryLabel
            x={
              props.secondTitleParams !== undefined
                ? props.secondTitleParams.x
                : undefined
            }
            y={
              props.secondTitleParams !== undefined
                ? props.secondTitleParams.y
                : undefined
            }
            text={
              props.secondTitleParams !== undefined
                ? props.secondTitleParams.title
                : undefined
            }
            style={{ fontSize: "14px" }}
          />
          {/* line */}
          <VictoryLine
            data={props.data.line}
            style={{
              data: {
                stroke: "#008BD2",
                strokeWidth: 1,
              },
            }}
          />
          {/* points */}
          <VictoryScatter
            data={props.data.point}
            style={lineStyles}
            size={2}
            labels={({ datum }) =>
              props.idChart !== 0
                ? datum.y.toFixed(1) +
                  " - " +
                  datum.x
                    .toLocaleString("default", {
                      day: "numeric",
                      month: "short",
                    })
                    .concat(
                      "\n" +
                        datum.x.toLocaleString("default", {
                          hour: "numeric",
                        })
                    )
                : datum.y.toFixed(1)
            }
            labelComponent={<VictoryTooltip />}
            events={eventsCss}
          />
          {/* OPTIONAL */}
          {/* Vertical line */}
          {props.verticalLine !== undefined &&
            props.verticalLine.length !== 0 &&
            verticalLinesRenders(props.verticalLine)}
          {/* point */}
          {props.stresHold !== undefined && (
            <VictoryScatter
              data={props.stresHold}
              style={{
                data: { fill: "#008BD2" },
                labels: {
                  fontWeight: 500,
                  fontSize: 10,
                },
              }}
              size={7}
              labels={() => [`Déversement`]}
              labelComponent={<VictoryLabel dx={40} dy={5} renderInPortal={true}/>}
            />
          )}
          {/* date points */}
          {props.dataDates !== undefined && (
            <VictoryScatter
              data={props.dataDates}
              style={lineStyles}
              size={5}
              events={eventsCss}
            />
          )}
        </VictoryChart>
      ) : (
        <span>{"Aucune données !"}</span>
      )}
    </>
  );
};

export default ChartLineComponent;
